import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface EnrichResult {
  success: boolean;
  noticeId: string;
  descriptionUpdated: boolean;
  documentsFound: number;
  error?: string;
}

export function useContractEnrichment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enrichContract = useCallback(async (
    noticeId: string,
    forceRefresh: boolean = false
  ): Promise<EnrichResult> => {
    setLoading(true);
    setError(null);

    try {
      console.log(`🔍 Enriching contract: ${noticeId}`);

      const { data, error: fnError } = await supabase.functions.invoke('enrich-contract', {
        body: { noticeId, forceRefresh }
      });

      if (fnError) {
        throw fnError;
      }

      if (!data) {
        throw new Error('No response from enrichment function');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      return data as EnrichResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Enrichment failed';
      setError(message);
      return {
        success: false,
        noticeId,
        descriptionUpdated: false,
        documentsFound: 0,
        error: message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if a contract needs enrichment
  const needsEnrichment = useCallback((contract: any): boolean => {
    const description = contract?.description || '';
    // Needs enrichment if description is just a URL or very short
    return description.startsWith('http') || description.length < 100;
  }, []);

  return {
    loading,
    error,
    enrichContract,
    needsEnrichment
  };
}
